import type { PaymentOption, v2GetCouponsParams } from "@be-miniapp/api"
import { PaymentOptionKey } from "@be-miniapp/types"

interface ResolvePreferredPaymentModeParams {
  paymentMethod?: PaymentOption
  preferredPaymentMethod?: number
  partnerDefaultPaymentMethod?: number
}

interface ResolveFarePaymentMethodParams {
  storePaymentMethod?: PaymentOption
  enrichedPaymentMethod?: PaymentOption
}

function isDisplayablePaymentOption(
  paymentMethod?: PaymentOption
): paymentMethod is PaymentOption {
  return Boolean(paymentMethod?.display_name?.trim())
}

/**
 * Prefer a store-selected payment option when it has display metadata.
 * Otherwise fall back to API-enriched options (e.g. after in-trip hydrate wrote a code-only shell).
 */
function resolveFarePaymentMethod({
  storePaymentMethod,
  enrichedPaymentMethod,
}: ResolveFarePaymentMethodParams): PaymentOption | undefined {
  if (isDisplayablePaymentOption(storePaymentMethod)) {
    return storePaymentMethod
  }

  return enrichedPaymentMethod ?? storePaymentMethod
}

/**
 * Resolves the payment mode sent to fare/promo APIs.
 * Matches fare calculation: explicit selection → partner default → stored preference → cash.
 */
function resolvePreferredPaymentMode({
  paymentMethod,
  preferredPaymentMethod,
  partnerDefaultPaymentMethod,
}: ResolvePreferredPaymentModeParams): number {
  if (paymentMethod?.payment_method != null) {
    return paymentMethod.payment_method
  }

  if (partnerDefaultPaymentMethod != null) {
    return partnerDefaultPaymentMethod
  }

  if (preferredPaymentMethod != null) {
    return preferredPaymentMethod
  }

  return PaymentOptionKey.CASH
}

type CouponPaymentRequestFields = Pick<
  v2GetCouponsParams,
  "prefer_payment_mode" | "business_id" | "card_id"
>

function getCouponPaymentRequestFields({
  paymentMethod,
  preferPaymentMode,
}: {
  paymentMethod?: PaymentOption
  preferPaymentMode: number
}): CouponPaymentRequestFields {
  return {
    prefer_payment_mode: preferPaymentMode,
    business_id:
      paymentMethod?.payment_method === PaymentOptionKey.COOPERATE
        ? paymentMethod.business_id
        : undefined,
    card_id:
      paymentMethod?.payment_method === PaymentOptionKey.PAY_BY_CARD ||
      paymentMethod?.payment_method === PaymentOptionKey.MPGS
        ? paymentMethod.cards_data?.id
        : undefined,
  }
}

export const comparePaymentMethod = (a: PaymentOption, b: PaymentOption) => {
  if (
    a.payment_method === PaymentOptionKey.PAY_BY_CARD ||
    b.payment_method === PaymentOptionKey.PAY_BY_CARD
  ) {
    return a.cards_data?.id === b.cards_data?.id
  }

  if (
    a.payment_method === PaymentOptionKey.COOPERATE &&
    b.payment_method === PaymentOptionKey.COOPERATE
  ) {
    return a.business_id === b.business_id
  }

  if (
    a.payment_method === b.payment_method &&
    a.display_name === b.display_name
  )
    return true

  return false
}

export {
  getCouponPaymentRequestFields,
  isDisplayablePaymentOption,
  resolveFarePaymentMethod,
  resolvePreferredPaymentMode,
}
export type {
  CouponPaymentRequestFields,
  ResolveFarePaymentMethodParams,
  ResolvePreferredPaymentModeParams,
}
